import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { isInZeloolApp } from '@/utils/tools';
import { Provider, ProviderType } from '../types';

interface OneTrustConfig {
  domainScript: string;
  isEnabled?: boolean;
}

export class OneTrustProvider implements Provider {
  type = ProviderType.ONETRUST;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: OneTrustConfig;
  private maxRetries = 20;
  private retryDelay = 500;

  constructor(config: OneTrustConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    // 默认在本地环境启用日志
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // app中不加载OneTrust Cookie
      if (isInZeloolApp()) {
        if (this.loggingEnabled) {
          logger.log('OneTrust: Skipping initialization in app environment');
        }
        this.initialized = true;
        return true;
      }

      // 加载OneTrust脚本
      // 为了减小对TBT的影响，把其加载的时序再往后延
      setTimeout(async () => {
        await this.loadOneTrustScript();
        // 设置OneTrust回调
        this.setupOneTrustCallback();
      }, 5000);

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize OneTrust:', error);
      return false;
    }
  }

  track(): void {
    // OneTrust主要用于Cookie同意管理，不需要track特定事件
    // 它通过回调函数自动处理同意状态更新
    if (this.loggingEnabled) {
      logger.log('OneTrust: Track method called - OneTrust handles consent automatically');
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  enableLog(): void {
    this.loggingEnabled = true;
  }

  disableLog(): void {
    this.loggingEnabled = false;
  }

  /**
   * 加载OneTrust脚本
   */
  private async loadOneTrustScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const oScript = document.createElement('script');
        oScript.type = 'text/javascript';
        oScript.id = 'ze-snippet';
        oScript.async = true;
        oScript.setAttribute('data-domain-script', this.config.domainScript);
        oScript.src = 'https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js';

        oScript.onload = () => {
          if (this.loggingEnabled) {
            logger.log('OneTrust: Script loaded successfully');
          }
          resolve();
        };

        oScript.onerror = () => {
          const error = 'Failed to load OneTrust script';
          logger.error('OneTrust:', error);
          reject(new Error(error));
        };

        document.head.appendChild(oScript);
      } catch (error) {
        logger.error('OneTrust: Error creating script element:', error);
        reject(error);
      }
    });
  }

  /**
   * 设置OneTrust更新回调
   */
  private setupOneTrustCallback(): void {
    let tryNum = 0;

    const addUpdateCb = () => {
      if (window.OneTrust && window.OneTrust.UpdateGCM && window.OnetrustActiveGroups) {
        if (this.loggingEnabled) {
          logger.log('OneTrust: Setting up consent update callback');
        }

        window.OneTrust.UpdateGCM(() => {
          this.handleConsentUpdate();
        });
      } else if (tryNum <= this.maxRetries) {
        tryNum++;
        if (this.loggingEnabled) {
          logger.log(
            `OneTrust: Waiting for OneTrust to load (attempt ${tryNum}/${this.maxRetries})`,
          );
        }
        setTimeout(() => {
          addUpdateCb();
        }, this.retryDelay);
      } else {
        logger.warn('OneTrust: Maximum retry attempts reached, OneTrust may not be available');
      }
    };

    addUpdateCb();
  }

  /**
   * 处理用户同意状态更新
   */
  private handleConsentUpdate(): void {
    try {
      if (!window.OnetrustActiveGroups) {
        if (this.loggingEnabled) {
          logger.warn('OneTrust: OnetrustActiveGroups not available');
        }
        return;
      }

      const groupList = window.OnetrustActiveGroups.split(',');

      if (this.loggingEnabled) {
        logger.log('OneTrust: Active consent groups:', groupList);
      }

      // C001 Strictly Necessary Cookies; C0002 Performance Cookies; C0003 Functional Cookies; C0004 Targeting Cookies
      const canUseAnalysis = groupList.includes('C0002');
      const canUseAd = groupList.includes('C0004');

      // 只更新用户同意的情况，避免对美国用户产生影响
      const sendData: Record<string, string> = {};

      if (canUseAnalysis) {
        sendData.analytics_storage = 'granted';
      }

      if (canUseAd) {
        sendData.ad_storage = 'granted';
        sendData.ad_user_data = 'granted';
        sendData.ad_personalization = 'granted';
      }

      if (canUseAnalysis || canUseAd) {
        if (window.gtag) {
          window.gtag('consent', 'update', sendData);

          if (this.loggingEnabled) {
            logger.log('OneTrust: Updated gtag consent with data:', sendData);
          }
        } else {
          logger.warn('OneTrust: gtag is not available');
        }
      } else if (this.loggingEnabled) {
        logger.log('OneTrust: No consent updates needed');
      }
    } catch (error) {
      logger.error('OneTrust: Error handling consent update:', error);
    }
  }
}
